import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import type { ShopHomeKind, ShopHomeSection } from '@/lib/api/shop-content-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { HomeBlockForm } from '@/components/retail/home-block-form'
import {
    blockCount,
    channelState,
    kindLabel,
} from '@/components/retail/home-form-model'
import { HomeCatalogProvider } from '@/components/retail/home-link-field'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '@/hooks/use-auth'
import { useShopHome } from '@/hooks/use-shop-content-query'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

// Bán lẻ › Trang chủ (README 34): 7 khối trang chủ web + Mini App. Thứ tự khối
// do code cố định; mỗi khối lưu riêng (PUT cả khối, có version chống ghi đè).
export const Route = createFileRoute('/_app/_authed/_sidebar/retail/home')({
    component: HomePage,
})

export function HomePage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('shop_content.write')
    const canCatalog = hasPermission('shop_catalog.read')
    const query = useShopHome()
    const sections = query.data?.data?.sections ?? []
    const [selected, setSelected] = useState<ShopHomeKind>('hero_slide')
    const [dirty, setDirty] = useState(false)
    const [pending, setPending] = useState<ShopHomeKind | null>(null)
    const section = sections.find((s) => s.kind === selected)

    const choose = (kind: ShopHomeKind) => {
        if (kind === selected) return
        if (dirty) setPending(kind)
        else setSelected(kind)
    }
    const discard = () => {
        if (pending) setSelected(pending)
        setDirty(false)
        setPending(null)
    }

    let body: ReactNode
    if (query.isLoading)
        body = (
            <p className="text-sm text-muted-foreground">
                {m.common_loading()}
            </p>
        )
    else if (query.isError)
        body = (
            <p className="text-sm text-destructive" role="alert">
                {m.shop_home_load_failed()}
            </p>
        )
    else
        body = (
            <div className="grid gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
                <div className="space-y-2">
                    <nav aria-label={m.shop_home_blocks_aria()}>
                        <ul className="space-y-1">
                            {sections.map((s, i) => (
                                <li key={s.kind}>
                                    <BlockButton
                                        section={s}
                                        index={i}
                                        selected={s.kind === selected}
                                        onClick={() => choose(s.kind)}
                                    />
                                </li>
                            ))}
                        </ul>
                    </nav>
                    <p className="text-xs text-muted-foreground">
                        {m.shop_home_order_note()}
                    </p>
                </div>
                <div className="min-w-0">
                    {section && (
                        <HomeBlockForm
                            key={`${section.kind}:${section.version}`}
                            section={section}
                            canWrite={canWrite}
                            onDirtyChange={setDirty}
                            onReload={() => void query.refetch()}
                        />
                    )}
                </div>
            </div>
        )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.shop_home_title_page()}
                subtitle={m.shop_home_page_subtitle()}
            />
            {canCatalog ? (
                <HomeCatalogProvider>{body}</HomeCatalogProvider>
            ) : (
                body
            )}
            <Dialog
                open={pending !== null}
                onOpenChange={(o) => !o && setPending(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{m.shop_home_discard_title()}</DialogTitle>
                        <DialogDescription>
                            {m.shop_home_discard_desc()}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            variant="outline"
                            onClick={() => setPending(null)}
                        >
                            {m.shop_home_discard_stay()}
                        </Button>
                        <Button variant="destructive" onClick={discard}>
                            {m.shop_home_discard_confirm()}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}

function Chip({
    label,
    state,
}: {
    label: string
    state: 'on' | 'off' | 'none'
}) {
    return (
        <span
            className={cn(
                'rounded border px-1.5 py-0.5 text-[11px]',
                state === 'on'
                    ? 'border-foreground/40 font-semibold text-foreground'
                    : 'text-muted-foreground',
            )}
        >
            {state === 'none' ? `${label} —` : label}
            <span className="sr-only">
                {state === 'on'
                    ? m.shop_home_chip_on()
                    : state === 'off'
                      ? m.shop_home_chip_off()
                      : m.shop_home_chip_none()}
            </span>
        </span>
    )
}

function BlockButton({
    section,
    index,
    selected,
    onClick,
}: {
    section: ShopHomeSection
    index: number
    selected: boolean
    onClick: () => void
}) {
    return (
        <button
            type="button"
            aria-current={selected ? 'true' : undefined}
            onClick={onClick}
            className={cn(
                'w-full cursor-pointer rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2',
                selected && 'border-primary bg-muted',
            )}
        >
            <span className="block text-sm font-medium">
                {index + 1}. {kindLabel(section.kind)}
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground">
                    {blockCount(section)}
                </span>
                <Chip
                    label={m.shop_home_chip_web()}
                    state={channelState(section, 'web')}
                />
                <Chip
                    label={m.shop_home_chip_miniapp()}
                    state={channelState(section, 'miniapp')}
                />
            </span>
        </button>
    )
}
